/**
 * The confidence order (ISSUE-1304): every ranked pick, most sure first, with
 * a 44px up and down control on each row so a fan can reorder with one thumb.
 * Moving a pick trades places with whatever pick holds the rank it lands on.
 * A locked pick shows its rank and does not move. Rows slide into their new
 * place rather than jumping, and stay still under reduced motion.
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type { typeTeamName } from "../pick/useTeamNames";
import {
  PICKEM_CONFIDENCE_EMPTY,
  PICKEM_CONFIDENCE_HEADING,
  PICKEM_CONFIDENCE_HINT,
  PICKEM_LOCKED_LABEL,
  pickemMoveDownLabel,
  pickemMoveUpLabel,
} from "./pickemCopy";
import { pickemTeam } from "./pickemFormat";
import type { typeConfidenceRow } from "./pickemGameModel";

const ArrowIcon = ({ direction }: { direction: "up" | "down" }): ReactNode => (
  <svg
    className="h-icon-sm w-icon-sm"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d={direction === "up" ? "m4 10 4-4 4 4" : "m4 6 4 4 4-4"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PickemConfidenceOrder = (props: {
  rows: readonly typeConfidenceRow[];
  teamsById: ReadonlyMap<string, typeTeamName>;
  disabled: boolean;
  onMove: (input: { eventId: string; direction: "up" | "down" }) => void;
}): ReactNode => (
  <section
    className="flex flex-col gap-2"
    data-testid="pickem-confidence-order"
    aria-labelledby="pickem-confidence-heading"
  >
    <h2 id="pickem-confidence-heading" className="text-body-strong text-ink">
      {PICKEM_CONFIDENCE_HEADING}
    </h2>
    <p className="text-caption text-ink-secondary">{PICKEM_CONFIDENCE_HINT}</p>
    {props.rows.length === 0 ? (
      <p className="text-body text-ink-secondary">{PICKEM_CONFIDENCE_EMPTY}</p>
    ) : (
      <ol className="flex flex-col gap-2">
        {props.rows.map((row) => {
          const team = pickemTeam(props.teamsById, row.teamId);
          return (
            <li
              key={row.eventId}
              className="flex items-center gap-2 rounded-md border border-hairline bg-surface px-3 py-1 transition-transform duration-200 motion-reduce:transition-none"
              data-testid={`pickem-confidence-row-${row.eventId}`}
              data-rank={row.rank}
            >
              <span className="w-8 text-body-strong text-ink tabular-nums">
                {row.rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-body text-ink">
                {team.name}
              </span>
              {row.isLocked ? (
                <span className="text-caption text-ink-secondary">
                  {PICKEM_LOCKED_LABEL}
                </span>
              ) : (
                <span className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={pickemMoveUpLabel(team.name)}
                    disabled={props.disabled || !row.canMoveUp}
                    data-testid={`pickem-confidence-up-${row.eventId}`}
                    onClick={() => {
                      props.onMove({ eventId: row.eventId, direction: "up" });
                    }}
                  >
                    <ArrowIcon direction="up" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={pickemMoveDownLabel(team.name)}
                    disabled={props.disabled || !row.canMoveDown}
                    data-testid={`pickem-confidence-down-${row.eventId}`}
                    onClick={() => {
                      props.onMove({ eventId: row.eventId, direction: "down" });
                    }}
                  >
                    <ArrowIcon direction="down" />
                  </Button>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    )}
  </section>
);
